/**
 * PIN Lock Service
 *
 * Manages barber PIN authentication for screen wake and app open scenarios.
 * - Stores the currently logged-in barber's PIN settings
 * - Verifies PIN entries
 * - Tracks lock state
 * - Handles auto-lock on visibility change
 */

import { barberDataService } from './barberDataService';
import { verifyBarberPin } from './barberAuthService';

// Persisted lock flag (2026-09-21, Jose bug): the locked state was React/in-memory only, so a
// browser refresh reset it to unlocked — a trivial lock bypass. Persist it and rehydrate on the
// station barber restore so the lock survives a reload.
const LOCK_FLAG_KEY = 'slikpos_station_locked';

export interface BarberPinSettings {
  barberId: string;
  barberName: string;
  // BEHAVIOR-PIN (2026-09-29): the raw PIN no longer lives on the client. We keep only a flag for
  // whether this barber HAS a PIN login; unlocking re-verifies server-side via barberSignIn.
  hasPin: boolean;
  requirePinOnWake: boolean;
  requirePinOnOpen: boolean;
  autoLogoutMinutes: number;
  staySignedIn?: boolean; // owner/manager opt-out of the PIN pad on their own device (Jose 2026-10-19)
  // AUTHORITY, carried on the session (Jose 2026-10-21): the operator's role/accessLevel/permissions are
  // captured at PIN login so usePermission can trust them INSTANTLY — without waiting on barberDataService
  // to load. Without this, right after an iPad wake isManager read false (fell back to the shared 'staff'
  // base account) and the ⚡ manager squeeze toggle vanished ("permissions distorted on the iPad").
  role?: string;
  accessLevel?: string;
  permissions?: Record<string, boolean>;
}

class PinLockService {
  private currentBarber: BarberPinSettings | null = null;
  private isLocked: boolean = false;
  private lastActivityTime: number = Date.now();
  private lockListeners: ((locked: boolean) => void)[] = [];
  private autoLockTimer: NodeJS.Timeout | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor() {
    // Initialize visibility change handler
    if (typeof document !== 'undefined') {
      this.visibilityHandler = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  /**
   * Set the current barber for PIN lock
   */
  setCurrentBarber(barber: BarberPinSettings | null) {
    this.currentBarber = barber;

    if (barber) {
      // STAY SIGNED IN (Jose 2026-10-21): if the operator chose "keep me signed in on this device",
      // NEVER auto-lock and NEVER restore a lingering lock on a reload/wake — that's the whole point.
      // Without this guard a stale LOCK_FLAG re-locked the screen on every wake, so the PIN pad kept
      // coming back even with the box checked. Also clear the flag so it can't re-trip later.
      if (barber.staySignedIn) {
        try { localStorage.removeItem(LOCK_FLAG_KEY); } catch { /* private mode */ }
        this.isLocked = false;
        return;
      }
      // Start auto-lock timer if configured
      this.startAutoLockTimer();

      // If requirePinOnOpen is set and PIN exists, lock immediately;
      // OR restore a lock that was active before a refresh (persisted flag). Either way the
      // screen must not be walk-up-usable after a reload. (Jose 2026-09-21)
      const wasLocked = (() => { try { return localStorage.getItem(LOCK_FLAG_KEY) === 'true'; } catch { return false; } })();
      if (barber.hasPin && (barber.requirePinOnOpen || wasLocked)) {
        this.lock();
      }
    } else {
      this.stopAutoLockTimer();
      this.unlock();
    }
  }

  /**
   * Get current barber settings
   */
  getCurrentBarber(): BarberPinSettings | null {
    return this.currentBarber;
  }

  /**
   * Check if PIN lock is active
   */
  isScreenLocked(): boolean {
    return this.isLocked;
  }

  /**
   * Lock the screen
   */
  lock() {
    if (this.currentBarber?.hasPin) {
      this.isLocked = true;
      try { localStorage.setItem(LOCK_FLAG_KEY, 'true'); } catch { /* private mode */ }
      this.notifyListeners();
    }
  }

  /**
   * Unlock the screen (after successful PIN entry)
   */
  unlock() {
    this.isLocked = false;
    this.lastActivityTime = Date.now();
    try { localStorage.removeItem(LOCK_FLAG_KEY); } catch { /* private mode */ }
    this.notifyListeners();
  }

  /**
   * Verify PIN and unlock if correct.
   * BEHAVIOR-PIN (2026-09-29): now verifies SERVER-SIDE (barberSignIn) against the hashed secret —
   * no raw PIN on the client. Only the locked barber's own PIN unlocks (barberId must match). Async.
   */
  async verifyPin(enteredPin: string): Promise<boolean> {
    if (!this.currentBarber?.hasPin) {
      this.unlock();
      return true;
    }
    try {
      const result = await verifyBarberPin(enteredPin);
      if (result.ok && result.barberId === this.currentBarber.barberId) {
        this.unlock();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Record activity (resets auto-lock timer)
   */
  recordActivity() {
    this.lastActivityTime = Date.now();
    this.startAutoLockTimer();
  }

  // BUSY GUARD (2026-10-06): a long operation (data import, checkout, etc.) runs for minutes with NO
  // user input events, so the idle-lock would fire mid-work — clearing the operator and, for imports,
  // flipping the Firestore instance (the "different Firestore instance" crash). While busyCount > 0 the
  // idle-lock defers. Always pair beginBusy() with endBusy() in a finally. Reentrant via the counter.
  private busyCount: number = 0;
  beginBusy() { this.busyCount++; this.recordActivity(); }
  endBusy() { this.busyCount = Math.max(0, this.busyCount - 1); this.recordActivity(); }
  isBusy(): boolean { return this.busyCount > 0; }
  /** Run an async operation with the idle-lock suppressed for its whole duration. */
  async withBusy<T>(fn: () => Promise<T>): Promise<T> {
    this.beginBusy();
    try { return await fn(); } finally { this.endBusy(); }
  }

  /**
   * Subscribe to lock state changes
   */
  subscribe(callback: (locked: boolean) => void): () => void {
    this.lockListeners.push(callback);
    // Immediately notify of current state
    callback(this.isLocked);

    return () => {
      this.lockListeners = this.lockListeners.filter(l => l !== callback);
    };
  }

  /**
   * Load barber PIN settings from Firebase
   */
  async loadBarberSettings(barberId: string): Promise<BarberPinSettings | null> {
    try {
      const barber = await barberDataService.getBarberById(barberId);
      if (!barber) return null;

      const settings: BarberPinSettings = {
        barberId: barber.id,
        barberName: `${barber.firstName || ''} ${barber.lastName || ''}`.trim() || 'Barber',
        hasPin: !!barber.hasPinLogin,
        requirePinOnWake: barber.requirePinOnWake ?? true,
        requirePinOnOpen: barber.requirePinOnOpen ?? true,
        autoLogoutMinutes: barber.autoLogoutMinutes ?? 15,
        role: (barber as any).role,
        accessLevel: (barber as any).accessLevel,
        permissions: (barber as any).permissions,
      };

      return settings;
    } catch (error) {
      console.error('Error loading barber PIN settings:', error);
      return null;
    }
  }

  /**
   * Handle visibility change (screen wake/sleep)
   */
  private handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Screen woke up
      if (this.currentBarber?.requirePinOnWake && this.currentBarber?.hasPin) {
        this.lock();
      }
    }
  }

  /**
   * Start auto-lock timer
   */
  private startAutoLockTimer() {
    this.stopAutoLockTimer();

    if (this.currentBarber?.autoLogoutMinutes && this.currentBarber.autoLogoutMinutes > 0) {
      const timeout = this.currentBarber.autoLogoutMinutes * 60 * 1000;
      this.autoLockTimer = setTimeout(() => {
        // Don't lock mid-operation (import/checkout/etc.) — re-arm and re-check. (2026-10-06)
        if (this.isBusy()) { this.startAutoLockTimer(); return; }
        if (this.currentBarber?.hasPin) {
          this.lock();
        }
      }, timeout);
    }
  }

  /**
   * Stop auto-lock timer
   */
  private stopAutoLockTimer() {
    if (this.autoLockTimer) {
      clearTimeout(this.autoLockTimer);
      this.autoLockTimer = null;
    }
  }

  /**
   * Notify all listeners of lock state change
   */
  private notifyListeners() {
    this.lockListeners.forEach(listener => listener(this.isLocked));
  }

  /**
   * Cleanup (call when service is no longer needed)
   */
  destroy() {
    this.stopAutoLockTimer();
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.lockListeners = [];
  }
}

export const pinLockService = new PinLockService();
export default pinLockService;
